import { api, unwrap } from "@lumberset/api-client";
import { supportQueries } from "@lumberset/support-ui";
import { queryOptions } from "@tanstack/react-query";

/** Query definitions, one per thing the app reads. Keys are what mutations invalidate. */

/** Whether an Admin has sign-up open. No session needed. */
export const signupStatusQuery = queryOptions({
  queryKey: ["signup-status"],
  queryFn: () => unwrap(api.public.signup.$get()),
  staleTime: 60_000,
});

export const yardQuery = queryOptions({
  queryKey: ["yard"],
  queryFn: () => unwrap(api.yard.$get()),
  staleTime: 60_000,
});

export const standardsQuery = queryOptions({
  queryKey: ["yard", "standards"],
  queryFn: () => unwrap(api.yard.standards.$get()),
});

export const billingQuery = queryOptions({
  queryKey: ["billing"],
  queryFn: () => unwrap(api.billing.$get()),
});

export const projectsQuery = (
  filters: { q?: string; status?: "active" | "won" | "lost" | "archived" } = {},
) =>
  queryOptions({
    queryKey: ["projects", filters],
    queryFn: () => unwrap(api.projects.$get({ query: filters })),
  });

export const projectQuery = (id: string) =>
  queryOptions({
    queryKey: ["projects", id],
    queryFn: () => unwrap(api.projects[":id"].$get({ param: { id } })),
  });

export const planSetQuery = (id: string) =>
  queryOptions({
    queryKey: ["plan-sets", id],
    queryFn: () => unwrap(api["plan-sets"][":id"].$get({ param: { id } })),
  });

export const takeoffQuery = (id: string) =>
  queryOptions({
    queryKey: ["takeoffs", id],
    queryFn: () => unwrap(api.takeoffs[":id"].$get({ param: { id } })),
  });

export const takeoffEventsQuery = (id: string) =>
  queryOptions({
    queryKey: ["takeoffs", id, "events"],
    queryFn: () => unwrap(api.takeoffs[":id"].events.$get({ param: { id }, query: {} })),
  });

export const customersQuery = (q?: string) =>
  queryOptions({
    queryKey: ["customers", q ?? ""],
    queryFn: () => unwrap(api.customers.$get({ query: { q } })),
  });

export const catalogQuery = (params: { q?: string; page: number }) =>
  queryOptions({
    queryKey: ["catalog", params],
    queryFn: () =>
      unwrap(
        api.catalog.$get({ query: { q: params.q, page: String(params.page), pageSize: "50" } }),
      ),
    placeholderData: (previous) => previous,
  });

export const quotesQuery = queryOptions({
  queryKey: ["quotes"],
  queryFn: () => unwrap(api.quotes.$get({ query: {} })),
});

export const quoteQuery = (id: string) =>
  queryOptions({
    queryKey: ["quotes", id],
    queryFn: () => unwrap(api.quotes[":id"].$get({ param: { id } })),
  });

/** The Budget Estimate of a released run: its latest revision priced with Reference Prices. */
export const budgetQuery = (runId: string, region = "US") =>
  queryOptions({
    queryKey: ["takeoffs", runId, "budget", region],
    queryFn: () =>
      unwrap(api.takeoffs[":id"].budget.$get({ param: { id: runId }, query: { region } })),
  });

export const rfqsQuery = queryOptions({
  queryKey: ["rfqs"],
  queryFn: () => unwrap(api.rfqs.$get()),
});

export const rfqQuery = (id: string) =>
  queryOptions({
    queryKey: ["rfqs", id],
    queryFn: () => unwrap(api.rfqs[":id"].$get({ param: { id } })),
  });

/** The RFQ as a Supplier sees it behind their link. No session. */
export const supplierRfqQuery = (token: string) =>
  queryOptions({
    queryKey: ["public-rfq", token],
    queryFn: () => unwrap(api.public.rfq[":token"].$get({ param: { token } })),
    retry: false,
  });

/** The Billing Plan and what the next Takeoff or Change Order costs. Every Member may read this. */
export const billingSummaryQuery = queryOptions({
  queryKey: ["billing", "summary"],
  queryFn: () => unwrap(api.billing.summary.$get()),
});

/** Custom plan offers waiting for this workspace's answer. Owners and Managers. */
export const billingOffersQuery = queryOptions({
  queryKey: ["billing", "offers"],
  queryFn: () => unwrap(api.billing.offers.$get()),
});

export const creditPacksQuery = queryOptions({
  queryKey: ["billing", "credit-packs"],
  queryFn: () => unwrap(api.billing["credit-packs"].$get()),
  staleTime: 60_000,
});

export const changeOrdersQuery = (projectId: string) =>
  queryOptions({
    queryKey: ["change-orders", { projectId }],
    queryFn: () => unwrap(api["change-orders"].$get({ query: { projectId } })),
  });

export const changeOrderQuery = (id: string) =>
  queryOptions({
    queryKey: ["change-orders", id],
    queryFn: () => unwrap(api["change-orders"][":id"].$get({ param: { id } })),
  });

/** Support Tickets this workspace opened. The keys are shared with the support screens. */
export const ticketsQuery = supportQueries.tickets(api);

export const ticketQuery = (id: string) => supportQueries.ticket(id, api);
